import React, { createContext, useContext, useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { Storage } from '../../constants/storage';
import { userApi } from '../../utils/api';
import { useSessionsStatus } from '../store/hooks';

const ChatPlatformStateContext = createContext(null);
const ChatPlatformActionsContext = createContext(null);

export function ChatPlatformProvider({
    children,
    persistRightPanel = true,
    initialActiveSessionId = null,
    initialIsEmbedded = false,
}) {
    const [activeSessionId, setActiveSessionId] = useState(initialActiveSessionId);
    const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);

    // --- Right Panel State (initialize first to use in other initializers) ---
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(() => {
        if (!persistRightPanel) return false;
        try {
            const saved = window.localStorage.getItem(Storage.right_panel_is_open);
            return saved !== null ? JSON.parse(saved) : true;
        } catch {
            return true;
        }
    });

    const [wasOpenBeforeResize, setWasOpenBeforeResize] = useState(() => {
        try {
            const savedState = localStorage.getItem(Storage.sidebar_is_open);
            if (savedState !== null) return JSON.parse(savedState);
        } catch {}
        return true;
    });

    const isMobile = windowWidth < 768;

    const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
        // Safe access to isMobile since it's defined above
        if (isMobile) return false;
        try {
            const savedState = localStorage.getItem(Storage.sidebar_is_open);
            if (savedState !== null) return JSON.parse(savedState);
        } catch (error) {
            console.warn('Could not parse sidebar state from localStorage:', error);
        }
        return true;
    });

    const [isStreaming, setIsStreaming] = useState(false);
    const [currentUser, setCurrentUser] = useState(() => userApi.getCached());

    // --- Sidebar/notification state ---
    const [showNotificationPanel, setShowNotificationPanel] = useState(false);
    const [notificationCount, setNotificationCount] = useState(0);
    const [editingSessionId, setEditingSessionId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [openMenuSessionId, setOpenMenuSessionId] = useState(null);
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [membershipLabel, setMembershipLabel] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const sessionsStatus = useSessionsStatus();
    const [isInitializing, setIsInitializing] = useState(
        () => sessionsStatus !== 'succeeded' && sessionsStatus !== 'failed',
    );

    const [isInputDisabled, setIsInputDisabled] = useState(false);
    const [isShareView, setIsShareView] = useState(false);
    const [isAdminView, setIsAdminView] = useState(false);
    const [isDraftChat, setIsDraftChat] = useState(false);
    const [sharedInfo, setSharedInfo] = useState(null);
    const [isEmbedded, setIsEmbedded] = useState(initialIsEmbedded);
    const [hasRightPanelContent, setHasRightPanelContent] = useState(false);

    // Track window width changes
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle sidebar transitions when moving between mobile and desktop
    const prevMobileRef = useRef(isMobile);
    useEffect(() => {
        const wasMobile = prevMobileRef.current;
        if (isMobile && !wasMobile) {
            // Entering mobile: save sidebar state and close it
            setWasOpenBeforeResize(isSidebarOpen);
            setIsSidebarOpen(false);
        } else if (!isMobile && wasMobile) {
            // Leaving mobile: restore sidebar state
            setIsSidebarOpen(wasOpenBeforeResize);
        }
        prevMobileRef.current = isMobile;
    }, [isMobile]);

    useEffect(() => {
        if (isMobile) return;
        try {
            localStorage.setItem(Storage.sidebar_is_open, JSON.stringify(isSidebarOpen));
        } catch {}
    }, [isSidebarOpen, isMobile]);

    useEffect(() => {
        if (sessionsStatus === 'succeeded' || sessionsStatus === 'failed') {
            setIsInitializing(false);
        }
    }, [sessionsStatus]);

    // --- Callback registration (complex handlers live in ChatApp) ---
    const chatCallbacksRef = useRef({
        handleNewChat: null,
        switchSession: null,
        startEditing: null,
        handleRenameSession: null,
        handleDeleteSession: null,
        handleNotificationClick: null,
        handleLoginSuccess: null,
        handleLogout: null,
        openUserSettings: null,
    });

    const registerChatCallbacks = useCallback((cbs) => {
        Object.assign(chatCallbacksRef.current, cbs);
    }, []);

    const actions = useMemo(() => ({
        setActiveSessionId,
        setIsSidebarOpen,
        setWasOpenBeforeResize,
        setIsStreaming,
        setCurrentUser,
        setShowNotificationPanel,
        setNotificationCount,
        setEditingSessionId,
        setEditingName,
        setOpenMenuSessionId,
        setIsLoginOpen,
        setIsUserMenuOpen,
        setMembershipLabel,
        setIsLoading,
        setIsInitializing,
        setIsInputDisabled,
        setIsShareView,
        setIsAdminView,
        setIsDraftChat,
        setSharedInfo,
        setIsEmbedded,
        setIsRightPanelOpen,
        setHasRightPanelContent,
        registerChatCallbacks,
        // No-op for legacy compatibility since these are now derived or handled internally
        setIsMobile: () => {}, 
    }), [registerChatCallbacks]);

    const state = useMemo(() => ({
        activeSessionId,
        isMobile,
        isSidebarOpen,
        wasOpenBeforeResize,
        isStreaming,
        currentUser,
        showNotificationPanel,
        notificationCount,
        editingSessionId,
        editingName,
        openMenuSessionId,
        isLoginOpen,
        isUserMenuOpen,
        membershipLabel,
        isLoading,
        isInitializing,
        isInputDisabled,
        isShareView,
        isAdminView,
        isDraftChat,
        sharedInfo,
        isEmbedded,
        isRightPanelOpen,
        hasRightPanelContent,
        chatCallbacksRef,
    }), [
        activeSessionId, isMobile, isSidebarOpen, wasOpenBeforeResize,
        isStreaming, currentUser, showNotificationPanel, notificationCount,
        editingSessionId, editingName, openMenuSessionId, isLoginOpen,
        isUserMenuOpen, membershipLabel, isLoading,
        isInitializing, isInputDisabled, isShareView, isAdminView,
        isDraftChat, sharedInfo, isEmbedded, isRightPanelOpen,
        hasRightPanelContent
    ]);

    return (
        <ChatPlatformActionsContext.Provider value={actions}>
            <ChatPlatformStateContext.Provider value={state}>
                {children}
            </ChatPlatformStateContext.Provider>
        </ChatPlatformActionsContext.Provider>
    );
}

export function useChatPlatform() {
    const state = useContext(ChatPlatformStateContext);
    const actions = useContext(ChatPlatformActionsContext);
    if (!state || !actions) {
        throw new Error('useChatPlatform must be used inside <ChatPlatformProvider>');
    }
    return { ...state, ...actions };
}

// Alias for useChatPlatform to maintain compatibility with legacy code
export const useLayout = useChatPlatform;

export function useChatPlatformActions() {
    const actions = useContext(ChatPlatformActionsContext);
    if (!actions) {
        throw new Error('useChatPlatformActions must be used inside <ChatPlatformProvider>');
    }
    return actions;
}

export default ChatPlatformStateContext;

